<template>
  <v-card class="pa-2" elevation="2" rounded="lg">
    <div class="d-flex align-center justify-space-between mb-2">
      <div class="text-subtitle-1 font-weight-medium">Filter</div>
      <v-btn variant="text" size="small" @click="resetAll">Reset</v-btn>
    </div>

    <v-expansion-panels variant="accordion" multiple>
      <!-- Journal -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          Journal title
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <FacetCheckboxList
              facet-key="j_title"
              :items="facets.journal"
              :selected="selected.j_title"
              @update:selected="(v) => selected.j_title = v"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Year range -->
      <v-expansion-panel>
        <v-expansion-panel-title>Date issued (Year)</v-expansion-panel-title>
        <v-expansion-panel-text>
          <div v-if="ranges.year.min !== null && ranges.year.max !== null">
            <div class="d-flex justify-space-between text-caption mb-1">
              <span>{{ selected.year[0] }}</span>
              <span>{{ selected.year[1] }}</span>
            </div>
            <v-range-slider
                v-model="selected.year"
                :min="ranges.year.min"
                :max="ranges.year.max"
                :step="1"
                thumb-label
                strict
            />
          </div>
          <div v-else class="text-caption text-medium-emphasis">
            No year range available.
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Volume -->
      <v-expansion-panel>
        <v-expansion-panel-title>Volume</v-expansion-panel-title>
        <v-expansion-panel-text>
          <FacetCheckboxList
              facet-key="volume"
              :items="facets.volume"
              :selected="selected.volume"
              @update:selected="(v) => selected.volume = v"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Issue -->
      <v-expansion-panel>
        <v-expansion-panel-title>Issue</v-expansion-panel-title>
        <v-expansion-panel-text>
          <FacetCheckboxList
              facet-key="heft"
              :items="facets.issue"
              :selected="selected.heft"
              @update:selected="(v) => selected.heft = v"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Essay -->
      <v-expansion-panel>
        <v-expansion-panel-title>Essay</v-expansion-panel-title>
        <v-expansion-panel-text>
          <FacetCheckboxList
              facet-key="aufsatz"
              :items="facets.essay"
              :selected="selected.aufsatz"
              @update:selected="(v) => selected.aufsatz = v"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Page range -->
      <v-expansion-panel>
        <v-expansion-panel-title>Page</v-expansion-panel-title>
        <v-expansion-panel-text>
          <div v-if="ranges.page.min !== null && ranges.page.max !== null">
            <div class="d-flex justify-space-between text-caption mb-1">
              <span>{{ selected.page[0] }}</span>
              <span>{{ selected.page[1] }}</span>
            </div>
            <v-range-slider
                v-model="selected.page"
                :min="ranges.page.min"
                :max="ranges.page.max"
                :step="1"
                thumb-label
                strict
            />
          </div>
          <div v-else class="text-caption text-medium-emphasis">
            No page range available.
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <div class="d-flex gap-2 mt-3">
      <v-btn block color="primary" @click="applyFilters">Apply filter</v-btn>
    </div>
  </v-card>
</template>

<script>
const FacetCheckboxList = {
  props: {
    items: {type: Array, default: () => []},
    selected: {type: Array, default: () => []},
    facetKey: {type: String, required: true},
    initialLimit: {type: Number, default: 8},
  },
  emits: ["update:selected"],
  data() {
    return {showAll: false, search: ""}
  },
  computed: {
    filtered() {
      const q = (this.search || "").toLowerCase().trim()
      if (!q) return this.items
      return this.items.filter(it => (it.value || "").toLowerCase().includes(q))
    },
    visibleItems() {
      if (this.showAll) return this.filtered
      return this.filtered.slice(0, this.initialLimit)
    },
    hasMore() {
      return this.filtered.length > this.initialLimit
    }
  },
  methods: {
    toggle(val) {
      const s = new Set(this.selected)
      if (s.has(val)) s.delete(val); else s.add(val)
      this.$emit("update:selected", Array.from(s))
    }
  },
  template: `
    <div>
      <v-text-field
          v-if="items.length > 12"
          v-model="search"
          density="compact"
          variant="outlined"
          hide-details
          placeholder="Search..."
          class="mb-2"
      />
      <div v-for="it in visibleItems" :key="it.value" class="d-flex align-center justify-space-between py-1">
        <v-checkbox
            :model-value="selected.includes(it.value)"
            @update:model-value="toggle(it.value)"
            density="compact"
            hide-details
            :label="it.value"
        />
        <v-badge :content="it.count" inline/>
      </div>

      <v-btn
          v-if="hasMore"
          variant="text"
          size="small"
          class="mt-1"
          @click="showAll = !showAll"
      >
        {{ showAll ? "Less" : "More" }}
      </v-btn>
    </div>
  `
}

export default {
  name: "CmFilter",
  components: {FacetCheckboxList},
  data() {
    return {
      facets: {journal: [], volume: [], issue: [], essay: []},
      ranges: {year: {min: null, max: null}, page: {min: null, max: null}},
      selected: {
        j_title: [],
        volume: [],
        heft: [],
        aufsatz: [],
        year: [null, null],
        page: [null, null],
      },
    }
  },
  mounted() {
    const script = document.getElementById("cm-filter-payload");
    if (!script) {
      console.warn("cm-filter-payload not found in DOM");
      return;
    }

    let payload = {};
    try {
      payload = JSON.parse(script.textContent || "{}");
    } catch (e) {
      console.error("CM filter JSON parse failed. script content=", script.textContent);
      payload = {};
    }

    this.facets = payload.facets || this.facets;
    this.ranges = payload.ranges || this.ranges;

    if (this.ranges.year?.min != null && this.ranges.year?.max != null) {
      this.selected.year = [this.ranges.year.min, this.ranges.year.max];
    }
    if (this.ranges.page?.min != null && this.ranges.page?.max != null) {
      this.selected.page = [this.ranges.page.min, this.ranges.page.max];
    }
  },
  methods: {
    resetAll() {
      this.selected.j_title = []
      this.selected.volume = []
      this.selected.heft = []
      this.selected.aufsatz = []
      if (this.ranges.year?.min != null && this.ranges.year?.max != null) {
        this.selected.year = [this.ranges.year.min, this.ranges.year.max]
      }
      if (this.ranges.page?.min != null && this.ranges.page?.max != null) {
        this.selected.page = [this.ranges.page.min, this.ranges.page.max]
      }
      this.applyFilters(true) // reset => ohne fq
    },

    buildFqParams() {
      const fq = []

      // checkbox facets
      const addOrGroup = (field, values) => {
        if (!values || values.length === 0) return
        // (field:"a" OR field:"b")
        const parts = values.map(v => `${field}:"${String(v).replaceAll('"', '\\"')}"`)
        fq.push(`(${parts.join(" OR ")})`)
      }

      addOrGroup("j_title", this.selected.j_title)
      addOrGroup("volume", this.selected.volume)
      addOrGroup("heft", this.selected.heft)
      addOrGroup("aufsatz", this.selected.aufsatz)

      // year range -> dateIssued range
      const [y1, y2] = this.selected.year
      if (y1 != null && y2 != null && (y1 !== this.ranges.year.min || y2 !== this.ranges.year.max)) {
        const from = `${y1}-01-01T00:00:00Z`
        const to = `${y2}-12-31T23:59:59Z`
        fq.push(`dateIssued:[${from} TO ${to}]`)
      }

      // page range
      const [p1, p2] = this.selected.page
      if (p1 != null && p2 != null && (p1 !== this.ranges.page.min || p2 !== this.ranges.page.max)) {
        fq.push(`page:[${p1} TO ${p2}]`)
      }

      return fq
    },

    applyFilters(isReset = false) {
      const url = new URL(window.location.href)
      const params = url.searchParams

      // immer auf Seite 1 zurück
      params.set("page", "1")

      // alte fq entfernen
      params.delete("fq")

      if (!isReset) {
        const fq = this.buildFqParams()
        fq.forEach(x => params.append("fq", x))
      }

      window.location.href = `${url.pathname}?${params.toString()}`
    }
  }
}
</script>
